class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    /** Set node selectors */
    this.shippingToDefaultSlot = this.querySelector('slot[name="shipping-to-default"]');
    this.shippingToSelectedSlot = this.querySelector('slot[name="shipping-to-selected"]');
    this.shippingToButton = this.querySelector('#shipping-location-btn');
    this.shippingToSelect = this.querySelector('#shipping-location-select');
    this.languageRadioTemplate = this.querySelector('#language-radio');
    this.localesSlot = this.querySelector('slot[name="locales"]');
    this.returnToElement = this.querySelector('input[name="return_to"]');

    /** Set configuration and helpers */
    this.currencySymbolMap = {};
    Object.entries(CURRENCY_CONFIGURATION).forEach(([handle, configuration]) => {
      this.currencySymbolMap[configuration.currency_iso_code] = configuration.currency_symbol;
    });
    this.regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

    /* Set initial state */
    this.shippingToSelectedOrigText = this.shippingToSelectedSlot.textContent;
    this.clientLocale = window.navigator.language.split('-')[0];
    this.initialState = {
      countryCode: this.shippingToSelect.dataset.selected,
      localeCode: this.localesSlot.querySelector('input[name="locale_code"]:checked')?.value,
    };
    this.blacklistedUrls = this.getAttribute('blacklisted-urls')?.split(',').map((url) => url.trim()) || [];
    this.redirectUrl = this.getAttribute('redirect-url') || '';

    this.currentState = Object.assign({}, this.initialState);
    this.initChosenLanguage();

    /* Set event listeners */
    this.handleRadioChange = function (e) {
      this.currentState.localeCode = e.currentTarget.value;
      this.updateReturnTo(this.returnToElement.value, { language: this.currentState.localeCode });
    };
    this.handleRadioChangeHandler = this.handleRadioChange.bind(this);
    this.bindRadioEvents();

    this.shippingToSelect.addEventListener('change', (e) =>
      this.handleShippingCountryChange(e.currentTarget.options[e.currentTarget.options.selectedIndex].id),
    );

    /**
     * If the browser supports the `showPicker` method for select elements, add
     * a click event listener on the button to show the picker.
     * - Helps with keyboard navigation and accessibility, where the user can
     *   focus on the button and open the select element, as the select element
     *   is visually hidden.
     */
    if (this.shippingToButton && 'showPicker' in this.shippingToSelect) {
      this.shippingToButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.shippingToSelect.showPicker();
      });
    }

    this.querySelector('form').addEventListener('submit', this.handleSubmit.bind(this));
    window.addEventListener('product:change', (e) => this.handleProductChange(e));

    /* Set initial UI state */
    this.shippingToSelectedSlot.style.display = 'none';
    this.updateSelectInput(this.currentState.countryCode);

    const clientLanguageElement = this.localesSlot.querySelector(`#language-${this.clientLocale}`);
    if (this.clientLocale !== Shopify.locale && clientLanguageElement) {
      Array.from(this.localesSlot.children).forEach((element) => element.classList.remove('order-first'));
      clientLanguageElement.checked = true;
      clientLanguageElement.parentElement.classList.add('order-first');
      this.currentState.localeCode = this.clientLocale;
    }

    this.querySelectorAll('slot[name="shipping-location-country"]').forEach((slot) => {
      slot.textContent = this.regionNames.of(this.currentState.countryCode);
    });

    if (this.blacklistedUrls.includes(this.returnToElement.value)) {
      this.returnToElement.value = this.redirectUrl;
    }
    this.updateReturnTo(this.returnToElement.value, { language: this.currentState.localeCode });

    if (this.chosenLocale === Shopify.locale) {
      this.selectLanguage(this.chosenLocale);
    }

    this.updateSupportedLanguages(this.currentState.countryCode);

    const drawerId = this.getAttribute('drawer');
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('drawer') === drawerId) {
      const drawer = document.getElementById(drawerId);
      if (drawer) {
        drawer.show();
      }
    }

    if (urlParams.get('suggestedCountry')) {
      const suggestedCountry = urlParams.get('suggestedCountry');
      this.updateSelectInput(suggestedCountry);
      this.handleShippingCountryChange(suggestedCountry);
    }
  }

  /**
   * Update the selected option in the shipping to select input.
   * @param {String} countryCode - Country code to select in the dropdown
   */
  updateSelectInput(countryCode) {
    this.shippingToSelect.selectedIndex = Array.from(this.shippingToSelect.options).findIndex(
      (option) => option.id === countryCode,
    );
  }

  handleProductChange(e) {
    this.updateReturnTo(`/products/${e.detail.product.handle}`, { language: this.currentState.localeCode });
  }

  handleShippingCountryChange(countryIsoCode) {
    this.updateShippingToSelectedSlot(countryIsoCode);
    this.updateSupportedLanguages(countryIsoCode);
    this.updateState(countryIsoCode);
    this.updateFormData(countryIsoCode);
    this.bindRadioEvents();
  }

  handleSubmit(e) {
    const country = this.shippingToSelect.options[this.shippingToSelect.selectedIndex].id;
    window.countryNav.suppressWrongSitePopup(country);
  }

  updateState(countryIsoCode) {
    this.currentState.countryCode = countryIsoCode;
    this.currentState.localeCode = this.localesSlot.querySelector('input[name="locale_code"]:checked').value;
  }

  updateShippingToSelectedSlot(countryIsoCode) {
    const foundCurrencyCountry = Object.entries(window.currencyCountryMap).find(([currencyCode, countries]) =>
      countries.includes(countryIsoCode),
    );
    if (!foundCurrencyCountry) {
      throw new Error('No currency code found for country: ' + countryIsoCode);
    }

    const [currencyIsoCode, currencySymbol] = foundCurrencyCountry;
    const shortcodes = {
      __COUNTRY__: this.regionNames.of(countryIsoCode),
      __CURRENCY_ISO_CODE__: currencyIsoCode,
      __CURRENCY_SYMBOL__: this.currencySymbolMap[currencyIsoCode],
    };
    let shippingToSelectedText = this.shippingToSelectedOrigText;
    Object.entries(shortcodes).forEach(([shortcode, value]) => {
      shippingToSelectedText = shippingToSelectedText.replace(shortcode, value);
    });
    this.shippingToSelectedSlot.textContent = shippingToSelectedText;
    this.shippingToDefaultSlot.style.display = 'none';
    this.shippingToSelectedSlot.style.display = 'block';
    this.querySelectorAll('slot[name="shipping-location-country"]').forEach((slot) => {
      slot.textContent = this.regionNames.of(countryIsoCode);
    });
  }

  updateSupportedLanguages(countryIsoCode) {
    /**
     * Get supported language from the `supportedLanguages` object and filter
     * out ones that don't match the country code in their supported countries.
     */
    const supportedLanguages = Object.entries(window.supportedLanguages).filter(([languageCode, countries]) =>
      countries.includes(countryIsoCode),
    );

    // Map supported languages entries array to an array of language codes.
    const languageCodes = supportedLanguages.map(([languageCode]) => languageCode);

    // Check if there are elements for the current languages already in the DOM.
    const languagesToRemove = Array.from(this.localesSlot.children).filter(
      (element) => !languageCodes.includes(element.querySelector('input[name="locale_code"]').value),
    );
    languagesToRemove.forEach((languageToRemove) => languageToRemove.remove());

    languageCodes.forEach((languageCode) => {
      const hasLanguageElement = this.localesSlot.querySelector(`#language-${languageCode}`);

      if (hasLanguageElement) {
        const item = hasLanguageElement.closest('label');
        item?.classList.remove('order-first');
        return;
      }

      const languageNames = new Intl.DisplayNames([languageCode], {
        type: 'language',
      });
      const templateContent = this.languageRadioTemplate.content.cloneNode(true);
      const item = templateContent.querySelector('label')
      const radioLabel = item.querySelector('[data-template="language-label"]');
      const radio = item.querySelector('input[name="locale_code"]');

      radio.id = `language-${languageCode}`;
      radio.value = languageCode;
      if (this.chosenLocale === languageCode) {
        radio.checked = true;
        item.classList.add('order-first');
      } else if (this.clientLocale === languageCode) {
        radio.checked = true;
        item.classList.add('order-first');
      } else if (Shopify.locale === languageCode) {
        radio.checked = true;
        item.classList.add('order-first');
      } else {
        radio.checked = false;
        item.classList.remove('order-first');
      }

      radioLabel.textContent = languageNames.of(languageCode);
      radioLabel.removeAttribute('data-template');

      this.localesSlot.appendChild(templateContent);
    });

    const hasCheckedInput = Boolean(this.localesSlot.querySelector('input[name="locale_code"]:checked'));

    // If there is only one language available, or no checked items, check the first one.
    if (this.localesSlot.children.length === 1 || !hasCheckedInput) {
      this.localesSlot.children[0].querySelector('input[name="locale_code"]').checked = true;
    }
  }

  selectLanguage(languageCode) {
    const selectedElement = Array.from(this.localesSlot.children).find(
      (element) => element.querySelector('input[name="locale_code"]').value === languageCode,
    );
    if (selectedElement) {
      Array.from(this.localesSlot.children).forEach((element) => element.classList.remove('order-first'));
      selectedElement.classList.add('order-first');
      selectedElement.querySelector('input[name="locale_code"]').checked = true;
    }
  }

  updateFormData(countryIsoCode) {
    const selectedOption = this.shippingToSelect.querySelector(`#${countryIsoCode}`);
    if (selectedOption.value.includes('kr.representclo.com')) {
      const currentUrl = encodeURIComponent(window.location.href);
      const windowLocationHref =
        selectedOption.value +
        (selectedOption.value.includes('?') ? '&' : '?') +
        'utm_source=' +
        currentUrl +
        '&utm_medium=redirect';
      this.querySelector('form').setAttribute('action', windowLocationHref);
    } else {
      this.querySelector('form').setAttribute('action', `${selectedOption.value}/localization`);
    }
    this.querySelector('input[name="country_code"]').value = countryIsoCode;
    this.updateReturnTo(this.returnToElement.value, { language: this.currentState.localeCode });
  }

  updateReturnTo(url, params = {}) {
    const urlObject = new URL(url, window.location.origin);

    /**
     * Remove `drawer` and `suggestedCountry` from the URL params if they exist.
     * - Prevents the drawer and suggested country from being passed to the
     *   returnTo URL when the user changes stores.
     */
    urlObject.searchParams.delete('drawer');
    urlObject.searchParams.delete('suggestedCountry');

    this.returnToElement.value = window.updateQueryParams(urlObject.pathname, params);
  }

  bindRadioEvents() {
    this.localesSlot.querySelectorAll('input').forEach((input) => {
      input.removeEventListener('change', this.handleRadioChangeHandler);
      input.addEventListener('change', this.handleRadioChangeHandler);
    });
  }

  hasStateChanged() {
    return JSON.stringify(this.initialState) !== JSON.stringify(this.currentState);
  }

  initChosenLanguage() {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1]);
    let localeCode = urlParams.get('language');

    if (localeCode) {
      cookies.setCookie('rep_chosen_language', localeCode);
    } else {
      localeCode = cookies.getCookie('rep_chosen_language');
    }

    this.chosenLocale = localeCode;
    return;
  }
}

customElements.define('localization-form', LocalizationForm);
